// @flow

import React from 'react';
import i18n from 'app/common/lib/i18n';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import ProfileCommunityUsers from 'app/common/react/components/profile/ProfileCommunity/ProfileCommunityUsers';
import ProfileEmptyState from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyState';
import ProfileEmptyStateText from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyStateText';
import SectionHeader from 'app/common/react/components/profile/ProfileCommunity/SectionHeader';
import withResource from 'app/common/lib/react/withResource';
import withRouteData from 'app/common/lib/react/withRouteData';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { type ProfileUser } from 'app/packages/pinterest-flow-types/user';
import { UserProfilePinGrid } from 'app/common/react/components/profile/UserProfilePinGrid/UserProfilePinGrid';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Container } from 'gestalt';

type Props = {|
  followers: {
    data: Array<ProfileUser>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  following: {
    data: Array<ProfileUser>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  params: {|
    userId?: number,
    username: string,
  |},
  pins: {
    data: Array<Object>,
    fetchMore: () => void,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  requestContext: RequestContextType,
  similarBusiness: {
    data: Array<ProfileUser>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  user: {
    data: ProfileUser,
  },
  viewer: Viewer,
|};

const SIMILAR_BUSINESS_PREVIEW_LIMIT = 4;

function ProfileCommunityContainer({
  followers,
  following,
  pins,
  requestContext,
  similarBusiness,
  user,
  viewer,
}: Props) {
  if (
    !user ||
    !user.data ||
    !followers ||
    !followers.data ||
    !following ||
    !following.data ||
    !similarBusiness ||
    !similarBusiness.data
  ) {
    return null;
  }

  const { follower_count, user_following_count, username, show_discovered_feed } = user.data;

  const isOwnProfile = viewer.isAuth && viewer.username === username;

  const pinnerFollowerCountString = interpolateNamedTemplate(
    i18n.ngettext('{{count}} Follower', '{{count}} Followers', follower_count),
    {
      count: <PrettyNumber value={follower_count} shortform shortformMaximumFractionDigits={1} />,
    },
  );
  const pinnerFollowingCountString = interpolateNamedTemplate(
    i18n.ngettext('{{count}} Following', '{{count}} Following', user_following_count),
    {
      count: (
        <PrettyNumber value={user_following_count} shortform shortformMaximumFractionDigits={1} />
      ),
    },
  );

  const peopleAlsoViewedString = i18n._(
    'People also visit',
    'Business people visited similar to this business',
  );

  if (
    (isOwnProfile &&
      !new SingleTreatmentExp(requestContext, 'bizpro_web_self_view_ia').enabled()) ||
    (!isOwnProfile &&
      !new SingleTreatmentExp(requestContext, 'bizpro_web_pinner_view_ia').enabled())
  ) {
    return null;
  }

  const hasPins = pins.data && pins.data.length > 0;

  return (
    <Box alignItems="center">
      {followers.data.length === 0 &&
        following.data.length === 0 &&
        similarBusiness.data.length === 0 && (
          <ProfileEmptyState>
            <ProfileEmptyStateText surface="community" user={user.data} />
          </ProfileEmptyState>
        )}
      {followers &&
        followers.data &&
        followers.data.length > 0 && (
          <ProfileCommunityUsers
            users={followers}
            isOwnProfile={isOwnProfile}
            title={pinnerFollowerCountString}
          />
        )}
      {following &&
        following.data &&
        following.data.length > 0 && (
          <ProfileCommunityUsers
            users={following}
            isOwnProfile={isOwnProfile}
            title={pinnerFollowingCountString}
          />
        )}

      {similarBusiness &&
        similarBusiness.data &&
        similarBusiness.data.length > 0 && (
          <ProfileCommunityUsers
            users={similarBusiness}
            isOwnProfile={isOwnProfile}
            title={peopleAlsoViewedString}
            previewUserLimit={SIMILAR_BUSINESS_PREVIEW_LIMIT}
          />
        )}
      {show_discovered_feed &&
        hasPins && (
          <Box>
            <Container>
              <Box padding={3}>
                <SectionHeader
                  label={i18n._(
                    'People are saving',
                    'Label for domain pins on user community page',
                  )}
                />
              </Box>
              <Box maxWidth={820} width="100%" alignContent="start">
                <UserProfilePinGrid
                  isOverview
                  isOwnProfile={isOwnProfile}
                  user={user.data}
                  username={username}
                  pins={pins}
                  viewer={viewer}
                />
              </Box>
            </Container>
          </Box>
        )}
    </Box>
  );
}

export default compose(
  withViewer,
  withResource({
    name: 'UserResource',
    key: 'user',
    options: ({ params }) => ({
      username: params.username,
      field_set_key: 'profile',
    }),
  }),
  withResource({
    name: 'UserFollowersResource',
    key: 'followers',
    options: ({ params }) => ({
      hide_find_friends_rep: true,
      username: params.username,
    }),
  }),
  withResource({
    name: 'UserFollowingResource',
    key: 'following',
    options: ({ params }) => ({
      hide_find_friends_rep: true,
      username: params.username,
    }),
  }),
  withResource({
    name: 'UserSimilarBusinessesResource',
    key: 'similarBusiness',
    options: ({ params, user }) => ({
      hide_find_friends_rep: true,
      username: params.username,
      userId: params.userId || (user && user.data) ? user.data.id : undefined,
    }),
  }),
  withResource({
    name: 'UserDiscoveredPinsResource',
    key: 'pins',
    options: ({ params, user, viewer }) => ({
      exclude_add_pin_rep: true,
      username: params.username,
      user_id: params.userId || (user && user.data) ? user.data.id : undefined,
      field_set_key:
        viewer.isPartner && viewer.isAuth && viewer.username === params.username
          ? 'partner_grid_item'
          : 'grid_item',
    }),
  }),
  withRouteData({
    name: 'ProfileCommunity',
    routeContextLog: (context: Object, params: { username: string }) => {
      let userId;
      if ((context.user || {}).username === params.username) {
        userId = (context.user || {}).id;
      }
      return {
        object_id_str: userId,
        view_type: PCONST.thrift.ViewType.USER,
        view_parameter: PCONST.thrift.ViewParameterType.USER_COMMUNITY,
      };
    },
  }),
  withRequestContext,
)(ProfileCommunityContainer);
